import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import usePhotos from "../hooks/usePhotos";
import useSettings from "../hooks/useSettings";

export default function InstagramSection() {
  const { lang } = useLang();
  const photos = usePhotos();
  const settings = useSettings();
  const url = settings.instagram_url;
  const dishPhotos = [1, 2, 3, 4].map((n) => photos[`piatto-${n}`]).filter(Boolean);

  if (!url || dishPhotos.length === 0) return null;

  const handle = `@${url.split("?")[0].split("/").filter(Boolean).pop()}`;

  return (
    <section id="social" data-testid="instagram-section" className="py-24 lg:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(201,162,101,0.07),transparent_65%)]" />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="eyebrow">Social</span>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#FAF7F2]">
            {lang === "it" ? (
              <>Il gusto continua <span className="italic text-[#C9A265]">su Instagram</span></>
            ) : (
              <>The taste continues <span className="italic text-[#C9A265]">on Instagram</span></>
            )}
          </h2>
          <p className="mt-5 text-base text-[#B0A89C] leading-relaxed">
            {lang === "it"
              ? "I piatti del giorno, la vetrina delle lombate e la vita del Milano, quasi in diretta."
              : "The dishes of the day, the steak display and life at the Milano, almost live."}
          </p>
        </motion.div>

        <div className="mt-14 flex flex-wrap justify-center gap-5 lg:gap-7">
          {dishPhotos.map((src, i) => (
            <motion.a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`instagram-photo-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1 }}
              className={`group relative overflow-hidden rounded-2xl border border-[rgba(255,255,255,0.08)] hover:border-[rgba(201,162,101,0.5)] w-40 sm:w-52 lg:w-60 h-56 sm:h-72 lg:h-80 transition-all duration-500 ${
                i % 2 === 0 ? "lg:-rotate-2" : "lg:rotate-2"
              } hover:rotate-0 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(201,162,101,0.18)]`}
            >
              <img
                src={src}
                alt={lang === "it" ? `Piatto del Ristorante Milano ${i + 1}` : `Ristorante Milano dish ${i + 1}`}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1.2s]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0C0A]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Instagram size={20} className="text-[#FAF7F2]" />
              </div>
            </motion.a>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-12 text-center"
        >
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-follow-button"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#C9A265] text-[#0D0C0A] text-xs uppercase tracking-[0.25em] font-semibold font-cinzel rounded-full hover:bg-[#E0B87A] transition-colors duration-300"
          >
            <Instagram size={16} />
            {lang === "it" ? `Seguici ${handle}` : `Follow us ${handle}`}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
